import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import cx from 'classnames';
import { isEmpty, prop } from 'ramda';
import OutsideClickHandler from 'react-outside-click-handler';
import Select, { components } from 'react-select';

import ReactSelect, { DropdownIndicator } from 'components/shared/FormAdapters/FormDefault/ReactSelect';
import selectStyles from 'components/shared/FormAdapters/FormDefault/ReactSelect/index.module.css';
import Icon from 'components/shared/Icon';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import { ContentIndicator } from 'components/shared/Select/components/ContentIndicator';
import Tooltip from 'components/shared/Tooltip';
import { useEscAction } from 'hooks/useEscAction';
import { FormFieldControlledTooltip } from 'hooks/useFormFieldPopup';
import { debounce } from 'utils/debounce';
import noop from 'utils/noop';

import CreateOption from './CreateOption';
import styles from './index.module.css';

const filterOptions = (options = [], query = '') =>
  options.filter((option) => option.label && option.label.toLowerCase().includes(query.toLocaleLowerCase()));

function Select2({
  name,
  placeholder,
  loadOptions,
  additionalQueryParams,
  defaultOption,
  isClearable,
  onOptionActionClick,
  optionActionIcon,
  tooltipContent,
  selectRef: externalSelectRef,
  blockMenuClosing,
  isCreatable,
  onOpenOptionCreation,
  optionCreationProps = {},
  onChange,
  passingValueType = 'simple',
  autoFocus = false,
  readOnly,
  readOnlyReason,
  renderNoOptionsMessage = () => {
    return 'No options found';
  },
  externalOptions = [],
  useExternalOptions = false,
  onBlur,
  isLoadingExternal,
  menuPlacement = 'bottom',
  maxMenuHeight,
  size,
  optionSubtitleKey = 'subtitle',
}) {
  const [isDisplayed, setIsDisplayed] = useState(true);
  const [internalOptions, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(
    defaultOption && defaultOption.value && defaultOption.label ? defaultOption : null,
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(selectedValue?.label ?? '');
  const [isNewOptionCreationOpen, setIsNewOptionCreationOpen] = useState(false);
  const filteredExternalOptions = useMemo(
    () => filterOptions(externalOptions, inputValue),
    [externalOptions, inputValue],
  );
  const options = useExternalOptions ? filteredExternalOptions : internalOptions;

  const selectContainerRef = useRef();
  const internalSelectRef = useRef();
  const selectRef = externalSelectRef ?? internalSelectRef;
  const menuRef = useRef();
  useEscAction(
    blockMenuClosing
      ? noop
      : () => {
          handleCloseMenu();
        },
  );

  const handleSelectChange = (option, { action }) => {
    if (action === 'clear') {
      onChange(null);
      handleSetInputValue('');
      setSelectedValue(null);
      handleCloseMenu({ withoutInputReset: true });

      // dirty hack to remount select input
      // because for some reason the input won't update sometimes when menu is closed
      if (!isMenuOpen) {
        setTimeout(() => {
          setIsDisplayed(false);
          setIsDisplayed(true);
        }, 0);
      }
    }

    if (action === 'select-option') {
      const valueToPass = passingValueType === 'detailed' ? option : option?.value;
      onChange(valueToPass ?? null);

      setSelectedValue(option);
      handleSetInputValue(option?.label ?? '');
    }
  };

  const handleSetInputValue = (value) => {
    setInputValue(value);
  };

  const handleCloseMenu = ({ withoutInputReset = false } = {}) => {
    setIsMenuOpen(false);

    if (!withoutInputReset && selectedValue?.label) {
      handleSetInputValue(selectedValue.label);
    }

    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  useEffect(() => {
    if (autoFocus && selectRef.current) {
      setTimeout(() => {
        selectRef.current.focus();
      }, 0);
    }
  }, []);

  const fetchOptionsDebounced = useCallback(
    debounce(async (q, additionalQueryParams) => {
      try {
        setIsLoading(true);
        const options = await loadOptions(q, additionalQueryParams);
        setOptions(options);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);

        if (!(err instanceof axios.Cancel)) {
          throw err;
        }
      }
    }, 1000),
    [],
  );
  useEffect(() => {
    if (useExternalOptions) {
      return;
    }

    if (readOnly) {
      setOptions([]);
      return;
    }
    if (isMenuOpen) {
      fetchOptionsDebounced(inputValue, additionalQueryParams);
    }
  }, [isMenuOpen, inputValue, fetchOptionsDebounced, readOnly, useExternalOptions]);

  const handleOpenOptionCreation = () => {
    if (onOpenOptionCreation) {
      onOpenOptionCreation(inputValue);
    }
    handleCloseMenu({ withoutInputReset: true });
    setIsNewOptionCreationOpen(true);
  };

  const MenuMemoized = useCallback(
    (props) => (
      <Menu
        anchorRef={menuRef}
        showCreateOptionButton={isCreatable}
        onOpenOptionCreation={() => {
          handleOpenOptionCreation();
        }}
        optionCreationLabel={optionCreationProps.openOptionCreationLabel}
        {...props}
      />
    ),
    [isCreatable],
  );
  const OptionMemoized = useCallback(
    (props) => (
      <Option
        onActionClick={onOptionActionClick}
        subtitleKey={optionSubtitleKey}
        actionIcon={optionActionIcon}
        {...props}
      />
    ),
    [],
  );
  const DropdownIndicatorMemoized = useCallback(
    (props) => (
      <div className={styles.dropdownIndicatorContainer} onClick={isMenuOpen ? handleCloseMenu : undefined}>
        <DropdownIndicator {...props} />
      </div>
    ),
    [isMenuOpen],
  );

  if (readOnly) {
    return (
      <div data-tip data-for={name} tabIndex={readOnlyReason ? 0 : -1}>
        <ReactSelect
          readOnly
          value={selectedValue?.label}
          options={[{ value: selectedValue?.label, label: selectedValue?.label }]}
          isLoading={isLoadingExternal}
        />
        <Tooltip id={name} withMouseOver disable={!readOnlyReason}>
          {readOnlyReason}
        </Tooltip>
      </div>
    );
  }

  if (!isDisplayed) {
    return null;
  }

  return (
    <div
      className={cx(
        styles.container,
        isNewOptionCreationOpen && styles.containerOptionCreationOpen,
        readOnly && styles.containerReadOnly,
      )}
    >
      <OutsideClickHandler onOutsideClick={blockMenuClosing ? noop : handleCloseMenu}>
        <div
          ref={selectContainerRef}
          className={cx(
            selectStyles.reactSelectContainer,
            size && prop(`reactSelectContainerSize-${size}`, selectStyles),
          )}
        >
          <Select
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                if (!options || isEmpty(options)) {
                  event.preventDefault();
                  if (isCreatable && !isLoading && inputValue) {
                    handleOpenOptionCreation();
                  }
                }
              }
            }}
            isDisabled={readOnly}
            filterOption={useExternalOptions ? undefined : () => true} // filtering is carried out within option fetching endpoint
            isClearable={isClearable}
            id={name}
            classNamePrefix="react-select"
            placeholder={placeholder}
            value={selectedValue}
            onChange={handleSelectChange}
            options={options}
            ref={selectRef}
            components={{
              Menu: MenuMemoized,
              Option: onOptionActionClick ? OptionMemoized : components.Option,
              DropdownIndicator: DropdownIndicatorMemoized,
            }}
            onInputChange={(value, { action }) => {
              if (action === 'input-blur' || action === 'menu-close') {
                return;
              }

              if (action === 'set-value') {
                handleCloseMenu();
                return;
              }

              if (action === 'input-change') {
                handleSetInputValue(value);
              }
            }}
            onFocus={() => {
              if (isMenuOpen) {
                return;
              }

              if (selectedValue?.label) {
                const inputEl = selectRef.current?.select?.inputRef;
                if (inputEl) {
                  // move cursor to the end of the input
                  inputEl.selectionStart = inputEl.selectionEnd = inputEl.value.length;
                }
              }
              setIsMenuOpen(true);
            }}
            inputValue={isMenuOpen ? inputValue : selectedValue?.label}
            menuIsOpen={isMenuOpen}
            controlShouldRenderValue={false}
            openMenuOnFocus={false}
            isLoading={(isMenuOpen && isLoading) || isLoadingExternal}
            noOptionsMessage={renderNoOptionsMessage}
            onBlur={onBlur}
            menuPlacement={menuPlacement}
            maxMenuHeight={maxMenuHeight}
          />
        </div>
      </OutsideClickHandler>

      {isNewOptionCreationOpen && (
        <CreateOption
          onClose={() => setIsNewOptionCreationOpen(false)}
          onCreateOption={async (value, confirmed) => {
            const newOption = optionCreationProps.onCreate
              ? await optionCreationProps.onCreate(value, confirmed)
              : { value, label: value, confirmed, __isNew__: true };
            handleSelectChange(newOption, { action: 'select-option' });
            setIsNewOptionCreationOpen(false);
          }}
          title={optionCreationProps.title}
          placeholder={optionCreationProps.placeholder}
          initialValue={inputValue}
          withConfirmation={optionCreationProps.withConfirmation}
          confirmationTitle={optionCreationProps.confirmationTitle}
        />
      )}

      {tooltipContent && (
        <FormFieldControlledTooltip isOpen={isMenuOpen} anchorRef={selectContainerRef} content={tooltipContent} />
      )}
    </div>
  );
}

const Option = ({ children, data, onActionClick, actionIcon, subtitleKey, ...props }) => {
  const containerRef = useRef();

  return (
    <div ref={containerRef} className={styles.option}>
      <components.Option {...props} data={data}>
        <div className={styles.optionWrapper}>
          {children}
          {subtitleKey && data[subtitleKey] && (
            <div className={cx(styles.optionSubtitle, 'option__subtitle')}>{data[subtitleKey]}</div>
          )}
        </div>
        {data.optionActionAvailable && (
          <Icon
            component={actionIcon}
            width="14px"
            height="10px"
            className={styles.optionActionButton}
            onClick={(event) => {
              event.stopPropagation();
              onActionClick(data);
            }}
          />
        )}

        {data.showContentIndicator && <ContentIndicator isSharedPage={data?.shared} />}
      </components.Option>
    </div>
  );
};

const Menu = ({ children, anchorRef, showCreateOptionButton, optionCreationLabel, onOpenOptionCreation, ...props }) => {
  return (
    <>
      <components.Menu innerRef={anchorRef} {...props}>
        {children}
        {showCreateOptionButton && (
          <button className={styles.createOptionButton} type="button" onClick={onOpenOptionCreation}>
            {optionCreationLabel ?? 'Create option'}
          </button>
        )}
        <MenuActionsHelper />
      </components.Menu>
    </>
  );
};

export default Select2;
